import { TimeZoneDateFnsService } from 'app/core/services/time-zone-date-fns.service';
import { PdfCreditTypeHelper } from 'app/shared/enum-helpers/pdf-credit-type-helper';
import { PdfDeliveryFormTypeHelper } from 'app/shared/enum-helpers/pdf-delivery-form-type-helper';
import { PdfFittingFormTypeHelper } from 'app/shared/enum-helpers/pdf-fitting-form-type-helper';
import { PdfInvoiceTypeHelper } from 'app/shared/enum-helpers/pdf-invoice-type-helper';
import { PdfProFormaTypeHelper } from 'app/shared/enum-helpers/pdf-pro-forma-type-helper';
import { PdfQuoteTypeHelper } from 'app/shared/enum-helpers/pdf-quote-type-helper';
import { PdfWorkFormTypeHelper } from 'app/shared/enum-helpers/pdf-work-form-type-helper';
import { NotificationStatus } from 'app/shared/enums/notification-status.enum';
import { CustomDatePipe } from 'app/shared/pipes/custom-date.pipe';
import { ReadPdfMergeNotificationViewModel } from 'app/shared/prothesis-api-view-models/read-pdf-merge-notification.view-model';
import { buildNotificationCardTitle } from '../../build-notification-card-title';
import { PdfMergeNotificationCardModel } from './pdf-merge-notification-card.model';

export class PdfMergeNotificationModelHelper {
  public static build(): PdfMergeNotificationCardModel {
    const pdfMergeNotification = new PdfMergeNotificationCardModel();
    return pdfMergeNotification;
  }

  public static fromViewModel(
    readPdfMergeNotificationViewModels: ReadPdfMergeNotificationViewModel[],
    customDatePipe: CustomDatePipe,
    laboratoryLegalIdentifierSchemeCode: string,
  ): Array<PdfMergeNotificationCardModel> {
    const pdfMergeNotifications = new Array<PdfMergeNotificationCardModel>();
    readPdfMergeNotificationViewModels.forEach((readPdfMergeNotificationViewModel) => {
      const pdfMergeNotification = this._createModel(
        readPdfMergeNotificationViewModel,
        customDatePipe,
        laboratoryLegalIdentifierSchemeCode,
      );
      pdfMergeNotifications.push(pdfMergeNotification);
    });

    return pdfMergeNotifications;
  }

  public static fromViewModelSingle(
    readPdfMergeNotificationViewModel: ReadPdfMergeNotificationViewModel,
    customDatePipe: CustomDatePipe,
    laboratoryLegalIdentifierSchemeCode: string | null,
  ): PdfMergeNotificationCardModel {
    return this._createModel(readPdfMergeNotificationViewModel, customDatePipe, laboratoryLegalIdentifierSchemeCode);
  }

  private static _createModel(
    readPdfMergeNotificationViewModel: ReadPdfMergeNotificationViewModel,
    customDatePipe: CustomDatePipe,
    laboratoryLegalIdentifierSchemeCode: string | null,
  ): PdfMergeNotificationCardModel {
    const pdfMergeNotification = new PdfMergeNotificationCardModel();
    pdfMergeNotification.colorClass = 'notification-accent';
    pdfMergeNotification.discriminator = readPdfMergeNotificationViewModel.discriminator;
    pdfMergeNotification.notificationDate = TimeZoneDateFnsService.toLaboratoryTimeZone(readPdfMergeNotificationViewModel.notificationDate);
    pdfMergeNotification.readDate = TimeZoneDateFnsService.toLaboratoryTimeZone(readPdfMergeNotificationViewModel.readDate);
    pdfMergeNotification.title = buildNotificationCardTitle(
      'PDF multiple',
      readPdfMergeNotificationViewModel.notificationDate,
      customDatePipe,
    );

    if (readPdfMergeNotificationViewModel.creditPdfMergeNotifications.length > 0) {
      pdfMergeNotification.messageLine1 = PdfCreditTypeHelper.buildPdfMergeNotificationCardMessage(
        readPdfMergeNotificationViewModel.creditPdfMergeNotifications.length,
        readPdfMergeNotificationViewModel.creditPdfMergeNotifications[0].pdfDocumentType,
      );
    }
    if (readPdfMergeNotificationViewModel.deliveryFormPdfMergeNotifications.length > 0) {
      pdfMergeNotification.messageLine1 = PdfDeliveryFormTypeHelper.buildPdfMergeNotificationCardMessage(
        readPdfMergeNotificationViewModel.deliveryFormPdfMergeNotifications.length,
        readPdfMergeNotificationViewModel.deliveryFormPdfMergeNotifications[0].pdfDocumentType,
      );
    }
    if (readPdfMergeNotificationViewModel.fittingFormPdfMergeNotifications.length > 0) {
      pdfMergeNotification.messageLine1 = PdfFittingFormTypeHelper.buildPdfMergeNotificationCardMessage(
        readPdfMergeNotificationViewModel.fittingFormPdfMergeNotifications.length,
        readPdfMergeNotificationViewModel.fittingFormPdfMergeNotifications[0].pdfDocumentType,
      );
    }
    if (readPdfMergeNotificationViewModel.invoicePdfMergeNotifications.length > 0) {
      pdfMergeNotification.messageLine1 = PdfInvoiceTypeHelper.buildPdfMergeNotificationCardMessage(
        readPdfMergeNotificationViewModel.invoicePdfMergeNotifications.length,
        readPdfMergeNotificationViewModel.invoicePdfMergeNotifications[0].pdfDocumentType,
      );
    }
    if (readPdfMergeNotificationViewModel.proFormaPdfMergeNotifications.length > 0) {
      pdfMergeNotification.messageLine1 = PdfProFormaTypeHelper.buildPdfMergeNotificationCardMessage(
        readPdfMergeNotificationViewModel.proFormaPdfMergeNotifications.length,
        readPdfMergeNotificationViewModel.proFormaPdfMergeNotifications[0].pdfDocumentType,
      );
    }
    if (readPdfMergeNotificationViewModel.quotePdfMergeNotifications.length > 0) {
      pdfMergeNotification.messageLine1 = PdfQuoteTypeHelper.buildPdfMergeNotificationCardMessage(
        readPdfMergeNotificationViewModel.quotePdfMergeNotifications.length,
        readPdfMergeNotificationViewModel.quotePdfMergeNotifications[0].pdfDocumentType,
      );
    }
    if (readPdfMergeNotificationViewModel.workFormPdfMergeNotifications.length > 0) {
      pdfMergeNotification.messageLine1 = PdfWorkFormTypeHelper.buildPdfMergeNotificationCardMessage(
        readPdfMergeNotificationViewModel.workFormPdfMergeNotifications.length,
        readPdfMergeNotificationViewModel.workFormPdfMergeNotifications[0].pdfDocumentType,
      );
    }

    const hasInvoiceOrCreditFlag =
      readPdfMergeNotificationViewModel.invoicePdfMergeNotifications.length > 0 ||
      readPdfMergeNotificationViewModel.creditPdfMergeNotifications.length > 0;
    pdfMergeNotification.facturXBadgeFlag = hasInvoiceOrCreditFlag && laboratoryLegalIdentifierSchemeCode === '0009';
    pdfMergeNotification.ublBadgeFlag =
      hasInvoiceOrCreditFlag && !!laboratoryLegalIdentifierSchemeCode && laboratoryLegalIdentifierSchemeCode !== '0009';
    pdfMergeNotification.statusFailedFlag = readPdfMergeNotificationViewModel.status === NotificationStatus.Failed;
    pdfMergeNotification.statusNothingToDoFlag = readPdfMergeNotificationViewModel.status === NotificationStatus.NothingToDo;
    pdfMergeNotification.statusPendingFlag = readPdfMergeNotificationViewModel.status === NotificationStatus.Pending;
    pdfMergeNotification.statusSucceedFlag = readPdfMergeNotificationViewModel.status === NotificationStatus.Succeed;
    pdfMergeNotification.uuid = readPdfMergeNotificationViewModel.uuid;
    return pdfMergeNotification;
  }
}
